import React, { useRef, useEffect, useCallback } from 'react'
import { Box, Container, IconButton } from '@mui/material'
import { ChevronLeft, ChevronRight } from '@mui/icons-material'

import { FlightOffer } from '@/src/types/flightOffer'
import BannerCard from './BannerCard'

interface HomepageCarouselProps {
    locale: string
    offers: FlightOffer[]
    currentIndex: number
    scrollContainerRef: React.RefObject<HTMLDivElement>
    onPrev: () => void
    onNext: () => void
    onMouseEnter: () => void
    onMouseLeave: () => void
    onOfferClick: (slug: string | undefined) => void
    onMobileScroll: () => void
}

export const HomepageCarousel: React.FC<HomepageCarouselProps> = ({
    locale,
    offers,
    currentIndex,
    scrollContainerRef,
    onPrev,
    onNext,
    onMouseEnter,
    onMouseLeave,
    onOfferClick,
    onMobileScroll
}) => {
    const isRTL = locale === 'ar'

    if (!offers || offers.length === 0) {
        return null
    }

    // Determine arrow handlers based on RTL
    const leftArrowHandler = isRTL ? onPrev : onNext
    const rightArrowHandler = isRTL ? onNext : onPrev
    const leftArrowIcon = isRTL ? <ChevronRight fontSize="large" /> : <ChevronLeft fontSize="large" />
    const rightArrowIcon = isRTL ? <ChevronLeft fontSize="large" /> : <ChevronRight fontSize="large" />

    return (
        <Container maxWidth="lg" sx={{ px: { xs: 1, sm: 2 }, mb: 1 }}>
            <Box
                sx={{
                    position: 'relative',
                    mx: 'auto',
                    maxWidth: { xs: '100%', md: '1200px' },
                    direction: isRTL ? 'rtl' : 'ltr'
                }}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
            >
                {/* Desktop Navigation Arrows */}
                <DesktopArrow direction="left" onClick={leftArrowHandler}>
                    {leftArrowIcon}
                </DesktopArrow>

                <DesktopArrow direction="right" onClick={rightArrowHandler}>
                    {rightArrowIcon}
                </DesktopArrow>

                {/* Mobile Carousel */}
                <MobileCarousel
                    scrollContainerRef={scrollContainerRef}
                    offers={offers}
                    onOfferClick={onOfferClick}
                    onScroll={onMobileScroll}
                    isRTL={isRTL}
                />

                {/* Desktop Carousel */}
                <DesktopCarousel
                    offers={offers}
                    currentIndex={currentIndex}
                    onOfferClick={onOfferClick}
                    isRTL={isRTL}
                />
            </Box>
        </Container>
    )
}

// Desktop Navigation Arrow
const DesktopArrow: React.FC<{
    direction: 'left' | 'right'
    onClick: () => void
    children: React.ReactNode
}> = ({ direction, onClick, children }) => (
    <IconButton
        onClick={onClick}
        sx={{
            display: { xs: 'none', lg: 'flex' },
            position: 'absolute',
            [direction]: -60,
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 20,
            bgcolor: 'common.white',
            width: 48,
            height: 48
        }}
    >
        {children}
    </IconButton>
)

// Mobile Carousel - uses native scroll with auto-sync
const MobileCarousel: React.FC<{
    scrollContainerRef: React.RefObject<HTMLDivElement>
    offers: FlightOffer[]
    onOfferClick: (slug: string | undefined) => void
    onScroll: () => void
    isRTL: boolean
}> = ({ scrollContainerRef, offers, onOfferClick, onScroll, isRTL }) => {
    return (
        <Box
            ref={scrollContainerRef}
            onScroll={onScroll}
            sx={{
                display: { xs: 'flex', lg: 'none' },
                overflowX: 'auto',
                scrollSnapType: 'x mandatory',
                scrollBehavior: 'smooth',
                gap: 2,
                direction: isRTL ? 'rtl' : 'ltr',
                '&::-webkit-scrollbar': {
                    display: 'none'
                },
                scrollbarWidth: 'none',
                msOverflowStyle: 'none'
            }}
        >
            {offers.map((offer, index) => (
                <Box
                    key={`mobile-${offer.offerId}-${index}`}
                    sx={{
                        flex: '0 0 100%',
                        scrollSnapAlign: 'center',
                        minWidth: 0
                    }}
                >
                    <BannerCard offer={offer} onOfferClick={onOfferClick} />
                </Box>
            ))}
        </Box>
    )
}

// Desktop Carousel - sliding container like mobile, shows 3 cards
const DesktopCarousel: React.FC<{
    offers: FlightOffer[]
    currentIndex: number
    onOfferClick: (slug: string | undefined) => void
    isRTL: boolean
}> = ({ offers, currentIndex, onOfferClick, isRTL }) => {
    const wrapperRef = React.useRef<HTMLDivElement>(null)
    const containerRef = React.useRef<HTMLDivElement>(null)
    const [transform, setTransform] = React.useState('translateX(0)')

    // Duplicate offers to create seamless loop (add extra copies at the end for wrap-around)
    const duplicatedOffers = React.useMemo(() => {
        if (offers.length === 0) return []
        // Add enough duplicates to handle wrap-around when showing 3 cards
        return [...offers, ...offers, ...offers]
    }, [offers])

    // Calculate transform offset to show the correct 3 cards
    React.useEffect(() => {
        if (!wrapperRef.current || !containerRef.current) return

        const wrapper = wrapperRef.current
        const container = containerRef.current

        // Get the first card's actual width (accounting for flex and gap)
        const firstCard = container.children[0] as HTMLElement
        if (!firstCard) return

        const cardWidth = firstCard.offsetWidth
        const gap = 24 // gap: 3 = 24px (theme spacing)

        // Calculate offset - start from the second set of offers (index = offers.length)
        // This allows seamless wrap-around
        const baseIndex = offers.length + (currentIndex % offers.length)
        const offset = -baseIndex * (cardWidth + gap)

        // For RTL, reverse the direction
        const finalOffset = isRTL ? -offset : offset
        setTransform(`translateX(${finalOffset}px)`)
    }, [currentIndex, isRTL, offers.length])

    return (
        <Box
            ref={wrapperRef}
            sx={{
                display: { xs: 'none', lg: 'block' },
                position: 'relative',
                overflow: 'hidden',
                '&::before, &::after': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    bottom: 0,
                    width: '60px',
                    zIndex: 10,
                    pointerEvents: 'none'
                },
                '&::before': {
                    left: 0,
                    background: 'linear-gradient(to right, rgba(255, 255, 255, 0.95), transparent)'
                },
                '&::after': {
                    right: 0,
                    background: 'linear-gradient(to left, rgba(255, 255, 255, 0.95), transparent)'
                }
            }}
        >
            <Box
                ref={containerRef}
                sx={{
                    display: 'flex',
                    gap: 3,
                    alignItems: 'stretch',
                    transform: transform,
                    transition: 'transform 0.7s cubic-bezier(0.4, 0, 0.2, 1)',
                    willChange: 'transform'
                }}
            >
                {/* Render duplicated offers for seamless loop */}
                {duplicatedOffers.map((offer, index) => (
                    <Box
                        key={`desktop-${offer.offerId}-${index}`}
                        sx={{
                            flex: '0 0 calc(33.333% - 16px)',
                            minWidth: 0
                        }}
                    >
                        <BannerCard offer={offer} onOfferClick={onOfferClick} />
                    </Box>
                ))}
            </Box>
        </Box>
    )
}

export default HomepageCarousel
export type { HomepageCarouselProps }
